import React, { useEffect, useRef, useState } from "react";
import Webcam from "react-webcam";
import Base from "../../Config/Base";
import { Select, Spin } from "antd";
import { API_BASE_URL } from "../../Config/Config";
import { useNavigate } from "react-router-dom";

const AddProductInventory = () => {
  const webcamRef = useRef(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const [storesData, setStoresData] = useState([]);
  const [productsData, setProductsData] = useState([]);
  const [unitsData, setUnitsData] = useState([]);
  const [supplierData, setSupplierData] = useState([]);

  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  const [Description, setDescription] = useState("");
  const [batchNo, setBatchNo] = useState("");
  const [weight, setWeight] = useState("");
  const [mfgDate, setMfgDate] = useState("");
  const [expDate, setExpDate] = useState("");
  const [price, setPrice] = useState("");
  const [submittedBarcode, setSubmittedBarcode] = useState(null);

  const navigate = useNavigate();
  const [userDataSession, setUserDataSession] = useState({});

  useEffect(() => {
    const userDataString =
      sessionStorage.getItem("MisUserDetails") ||
      localStorage.getItem("MisUserDetails");
    if (userDataString) {
      const userData = JSON.parse(userDataString);
      setUserDataSession(userData);
    } else {
      navigate("/");
    }
  }, [navigate]);

  const handleStoreChange = (value) => setSelectedStore(value);
  const handleProductChange = (value) => setSelectedProduct(value);
  const handleUnitChange = (value) => setSelectedUnit(value);
  const handleSupplierChange = (value) => setSelectedSupplier(value);

  // Fetch Stores
  const GetStores = async () => {
    if (!userDataSession?.OrgId || !userDataSession?.UserId) return;
    try {
      const response = await fetch(
        `${API_BASE_URL}Settings/getUserStores?OrgId=${userDataSession.OrgId}&UserId=${userDataSession.UserId}`
      );
      const data = await response.json();
      if (data.ResultData && Array.isArray(data.ResultData)) {
        setStoresData(data.ResultData);
      }
    } catch (error) {
      console.error("Error fetching stores:", error);
    }
  };

  const GetUnits = async () => {
    if (!userDataSession?.OrgId) return;
    try {
      const response = await fetch(
        `${API_BASE_URL}Settings/getUnits?OrgId=${userDataSession.OrgId}`
      );
      const data = await response.json();
      if (data.ResultData && Array.isArray(data.ResultData)) {
        setUnitsData(data.ResultData);
      }
    } catch (error) {
      console.error("Error fetching units:", error);
    }
  };

  // Fetch Products
  const GetProducts = async () => {
    if (!userDataSession?.OrgId) return;
    try {
      const response = await fetch(`${API_BASE_URL}Product/GetStoreProducts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          OrgId: userDataSession.OrgId,
          Id: "0",
          CategoryId: "0",
          BarCode: "0",
          IsActive: 1,
          ProductId: 0,
          StoreId: 0,
          IsForSale: 1,
          UserId: userDataSession.UserId,
          Type: 0,
        }),
      });
      const data = await response.json();
      if (data.ResultData && Array.isArray(data.ResultData)) {
        setProductsData(data.ResultData);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  // Fetch Suppliers
  useEffect(() => {
    const fetchSuppliers = async () => {
      if (!userDataSession?.OrgId) return;
      setLoading(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}MobileSVC/GetSuppliers?OrgId=${userDataSession.OrgId}`
        );
        const data = await response.json();
        if (data.Status && Array.isArray(data.ResultData)) {
          setSupplierData(data.ResultData);
        }
      } catch (error) {
        console.error("Error fetching suppliers:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSuppliers();
  }, [userDataSession?.OrgId]);

  // Initial data load + Auto-select first store and product
  useEffect(() => {
    if (userDataSession?.OrgId && userDataSession?.UserId) {
      GetStores();
      GetUnits();
      GetProducts();
      setLoading(false);
    }
  }, [userDataSession]);

  // Auto-select first store and first product after data loads
  useEffect(() => {
    if (storesData.length > 0 && !selectedStore) {
      const firstStore = storesData[0];
      setSelectedStore(firstStore.StoreId);
    }
  }, [storesData]);

  useEffect(() => {
    if (productsData.length > 0 && !selectedProduct) {
      const firstProduct = productsData[0];
      setSelectedProduct(firstProduct.Id);
    }
  }, [productsData]);

  // Image compression
  const compressImage = (base64Str, maxSizeKB = 200) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = base64Str;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const MAX_WIDTH = 800;
        const scale = MAX_WIDTH / img.width;
        canvas.width = MAX_WIDTH;
        canvas.height = img.height * scale;

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        let quality = 0.9;
        let dataUrl;
        do {
          dataUrl = canvas.toDataURL("image/jpeg", quality);
          quality -= 0.05;
        } while (dataUrl.length / 1024 > maxSizeKB && quality > 0.1);

        resolve(dataUrl);
      };
    });
  };

  const capturePhoto = async () => {
    const imageSrc = webcamRef.current.getScreenshot();
    if (imageSrc) {
      const compressed = await compressImage(imageSrc, 200);
      setCapturedImage(compressed);
      setIsCameraOpen(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmittedBarcode(null);

    if (!selectedStore || !selectedProduct || !selectedSupplier || !price) {
      alert(
        "Please select Store, Product, Supplier and Price before submitting."
      );
      setLoading(false);
      return;
    }

    if (
      !capturedImage &&
      !window.confirm("No photo taken. Proceed without image?")
    ) {
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("OrgId", userDataSession.OrgId);
    formData.append("ProductId", selectedProduct);
    formData.append("StoreId", selectedStore);
    formData.append("Quantity", 1.0);
    formData.append("Weight", weight || "");
    formData.append("Price", price || "");
    formData.append("SupplierId", selectedSupplier || "");
    formData.append("Description", Description || "");
    formData.append("MfgDate", mfgDate || "");
    formData.append("ExpiryDate", expDate || "");
    formData.append("Status", 1);
    formData.append("CreatedBy", userDataSession.UserId);

    if (capturedImage) {
      const blob = await (await fetch(capturedImage)).blob();
      const fileName = `unit_${selectedProduct}_${Date.now()}.jpg`;
      formData.append(
        "file",
        new File([blob], fileName, { type: "image/jpeg" })
      );
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}UnitInventory/AddUnitProduct`,
        {
          method: "POST",
          body: formData,
        }
      );

      const result = await response.json();

      if (response.ok) {
        // alert(`Success! Unit added.\nBarcode: ${result.Barcode}`);
        setSubmittedBarcode(result.Barcode);
        // Reset form
        setCapturedImage(null);
        setSelectedStore(storesData.length > 0 ? storesData[0].StoreId : null);
        setSelectedProduct(productsData.length > 0 ? productsData[0].Id : null);
        setWeight("");
        setPrice("");
        setMfgDate("");
        setExpDate("");
        setDescription("");
      } else {
        alert("Error: " + (result.error || "Failed to add unit"));
      }
    } catch (error) {
      console.error(error);
      alert("Network error. Check connection.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Base>
      {loading && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75"
          style={{ zIndex: 9999 }}
        >
          <Spin size="large" tip="Loading..." />
        </div>
      )}

      <div className="page-header">
        <div className="page-title">
          <h4>Add Product</h4>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-lg-6 col-6 mb-3">
                <label className="form-label">
                  Store <span className="text-danger">*</span>
                </label>
                <Select
                  className="w-100"
                  value={selectedStore}
                  onChange={handleStoreChange}
                  options={storesData.map((s) => ({
                    value: s.StoreId,
                    label: s.Name,
                  }))}
                  placeholder="Select store"
                />
              </div>

              <div className="col-lg-6 col-6 mb-3">
                <label className="form-label">
                  Product <span className="text-danger">*</span>
                </label>
                <Select
                  className="w-100"
                  value={selectedProduct}
                  onChange={handleProductChange}
                  options={productsData.map((p) => ({
                    value: p.Id,
                    label: `${p.ProductName} (${p.BarCode})`,
                  }))}
                  placeholder="Select product"
                />
              </div>

              <div className="col-lg-6 col-6 mb-3">
                <label className="form-label">Supplier</label>
                <Select
                  className="w-100"
                  value={selectedSupplier}
                  onChange={handleSupplierChange}
                  options={supplierData.map((s) => ({
                    value: s.Id,
                    label: s.SupplierName,
                  }))}
                  placeholder="Select supplier (optional)"
                />
              </div>

              <div className="col-lg-6 col-6 mb-3">
                <label className="form-label">Price</label>
                <input
                  type="number"
                  className="form-control"
                  value={price}
                  onWheel={(e) => e.target.blur()}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="Enter price"
                />
              </div>

              {/* Hidden fields as per your code */}
              <div className="col-lg-6 mb-3 d-none">
                <label className="form-label">Weight</label>
                <input
                  type="number"
                  className="form-control"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  placeholder="Enter weight (optional)"
                />
              </div>

              <div className="col-lg-6 mb-3 d-none">
                <label className="form-label">Unit</label>
                <Select
                  className="w-100"
                  value={selectedUnit}
                  onChange={handleUnitChange}
                  options={unitsData.map((u) => ({
                    value: u.Id,
                    label: u.Name,
                  }))}
                  placeholder="Select unit (optional)"
                />
              </div>

              <div className="col-lg-6 mb-3 d-none">
                <label className="form-label">MFG Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={mfgDate}
                  onChange={(e) => setMfgDate(e.target.value)}
                />
              </div>

              <div className="col-lg-6 mb-3 d-none">
                <label className="form-label">EXP Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={expDate}
                  onChange={(e) => setExpDate(e.target.value)}
                />
              </div>

              <div className="col-12 mb-3">
                <label className="form-label">Notes</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={Description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Any additional notes..."
                />
              </div>
            </div>

            {/* Photo Section - After form fields, before Submit */}
            <div className="card mb-4">
              <div className="card-body text-center py-4">
                <h5 className="mb-4">Product Photo</h5>

                {!capturedImage && !isCameraOpen && (
                  <div
                    className="d-inline-block p-5 border rounded-3 bg-light cursor-pointer"
                    onClick={() => setIsCameraOpen(true)}
                    style={{ cursor: "pointer" }}
                  >
                    <i className="fa-solid fa-camera fa-4x text-primary"></i>
                    <p className="mt-3 text-muted mb-0">Click to open camera</p>
                  </div>
                )}

                {isCameraOpen && !capturedImage && (
                  <div>
                    <Webcam
                      ref={webcamRef}
                      screenshotFormat="image/jpeg"
                      width={350}
                      height={280}
                      videoConstraints={{
                        facingMode: { exact: "environment" },
                      }}
                      className="rounded shadow"
                    />
                    <div className="mt-3">
                      <button
                        type="button"
                        className="btn btn-success me-2"
                        onClick={capturePhoto}
                      >
                        <i className="fa-solid fa-camera me-2"></i>Capture
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setIsCameraOpen(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {capturedImage && (
                  <div>
                    <img
                      src={capturedImage}
                      alt="Captured product"
                      className="rounded shadow"
                      style={{ maxWidth: "300px", maxHeight: "300px" }}
                    />
                    <div className="mt-3">
                      <small className="text-success">
                        Size: {(capturedImage.length / 1024).toFixed(1)} KB
                      </small>
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-primary mt-3"
                      onClick={() => {
                        setCapturedImage(null);
                        setIsCameraOpen(true);
                      }}
                    >
                      <i className="fa-solid fa-camera-retro me-2"></i>Retake
                      Photo
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="text-center">
              <button type="submit" className="btn btn-success btn-md  px-5">
                Submit
              </button>
              {submittedBarcode && (
                <div className="mt-3 text-success">
                  <strong>Success! Barcode: {submittedBarcode}</strong>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </Base>
  );
};

export default AddProductInventory;
